import { FC } from "react";
import { Redeem } from "@mui/icons-material";
import { pad, toHex } from "viem";

import { useMetamask, useSystemContract, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import { ListAction, ListActionVariant } from "@framework/styled";
import {
  AddressType,
  ClaimStatus,
  ClaimType,
  type IClaim,
  type IContract,
  SystemModuleType,
  TokenType,
} from "@framework/types";
import ExchangeClaimFacetClaimABI from "@framework/abis/json/ExchangeClaimFacet/claim.json";
import ExchangeClaimFacetSpendABI from "@framework/abis/json/ExchangeClaimFacet/spend.json";
import { ZeroAddress } from "@framework/constants";

import { sorter } from "../../../../../utils/sorter";

export interface IClaimRedeemButtonProps {
  claim: IClaim;
  className?: string;
  disabled?: boolean;
  variant?: ListActionVariant;
}

export const ClaimRedeemButton: FC<IClaimRedeemButtonProps> = props => {
  const { claim, className, disabled, variant } = props;

  const { writeContractAsync } = useWriteContractWithSimulation();

  const metaFnWithContract = useSystemContract<IContract, SystemModuleType>(
    async (values: IClaim, systemContract: IContract) => {
      const params = {
        externalId: BigInt(values.id),
        expiresAt: Math.ceil(new Date(values.endTimestamp).getTime() / 1000),
        nonce: values.nonce,
        extra: pad(toHex(Math.ceil(new Date(values.endTimestamp).getTime() / 1000)), { size: 32 }),
        receiver: values.merchant!.wallet,
        referrer: ZeroAddress,
      };

      const item = values.item?.components
        .slice()
        .sort(sorter("id"))
        .map(component => ({
          tokenType: BigInt(Object.values(TokenType).indexOf(component.tokenType)),
          token: component.contract?.address,
          tokenId: BigInt(
            values.claimType === ClaimType.TEMPLATE
              ? (component.templateId || 0).toString()
              : component.token!.tokenId.toString(),
          ), // suppression types check with 0
          amount: BigInt(component.amount),
        }));

      return values.claimType === ClaimType.TEMPLATE
        ? writeContractAsync({
            address: systemContract.address as AddressType,
            abi: ExchangeClaimFacetClaimABI,
            functionName: "claim",
            args: [params, item, values.signature],
          })
        : writeContractAsync({
            address: systemContract.address as AddressType,
            abi: ExchangeClaimFacetSpendABI,
            functionName: "spend",
            args: [params, item, values.signature],
          });
    },
  );

  const { execute: metaFn } = useMetamask((values: IClaim) => {
    return metaFnWithContract(SystemModuleType.EXCHANGE, values);
  });

  const handleClick = () => {
    return metaFn(claim);
  };

  const date = new Date();

  return (
    <ListAction
      onClick={handleClick}
      icon={Redeem}
      message="form.tips.redeem"
      className={className}
      disabled={disabled || claim.claimStatus !== ClaimStatus.NEW || new Date(claim.endTimestamp) > date}
      dataTestId="ClaimRedeemButton"
      variant={variant}
    />
  );
};
